import { forwardRef, useEffect, useRef, useState } from 'react';

import { Box, Typography } from '@mui/material';
import type { TreeItemProps } from '@mui/x-tree-view-pro';

import { BlueprintIcon } from 'components/icons/blueprint/BlueprintIcon';

import { DECOMPOSITION_TREE_TEST_ID } from '../../testIds';
import type { ExtraItemProps } from '../../types';
import { getDataNodeIdFromTreeId, isSelfOrDescendant } from '../../utils/treeId';
import { NodeActionMenu } from '../NodeActionMenu';
import { CustomTreeItemStyled, TREE_ITEM_MIN_HEIGHT, stripExtraItemProps } from './CustomTreeItemStyled';
import { NodeTitle } from './NodeTitle';

type DataNodeItemProps = TreeItemProps & ExtraItemProps;

export const DataNodeItem = forwardRef<HTMLLIElement, DataNodeItemProps>(function DataNodeItem(props, ref) {
  const {
    itemMetaMap,
    callbacks,
    nodeIcon,
    nodeName,
    isMoveMode,
    selectedMoveNodeId,
    selectedParentId,
    onSelectParent,
    disabledParentTreeId,
    testIdScope
  } = props;
  const treeItemProps = stripExtraItemProps(props);
  const { itemId } = treeItemProps;
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const isInMoveMode = Boolean(isMoveMode);

  const movedNodeRowRef = useRef<HTMLDivElement | null>(null);
  const isMovedNodeRow = isInMoveMode && selectedMoveNodeId === itemId;

  useEffect(() => {
    if (isMovedNodeRow) movedNodeRowRef.current?.scrollIntoView({ block: 'center' });
  }, [isMovedNodeRow, selectedParentId]);

  const meta = itemMetaMap?.[itemId];
  const dataNodeId = meta?.dataNodeId ?? getDataNodeIdFromTreeId(itemId);
  const showMenu = callbacks && !isInMoveMode;

  const isDisabledAsParent =
    isInMoveMode &&
    (itemId === disabledParentTreeId || (!!selectedMoveNodeId && isSelfOrDescendant(itemId, selectedMoveNodeId)));

  return (
    <CustomTreeItemStyled
      {...treeItemProps}
      ref={ref}
      data-testid={DECOMPOSITION_TREE_TEST_ID.item(dataNodeId, testIdScope)}
      data-move-selected={Boolean(isMovedNodeRow)}
      data-menu-open={Boolean(isMenuOpen)}
      slotProps={{
        iconContainer: {
          'data-testid': DECOMPOSITION_TREE_TEST_ID.expandButton(dataNodeId, testIdScope)
        } as React.HTMLAttributes<HTMLDivElement>
      }}
      label={
        <Box
          ref={movedNodeRowRef}
          data-testid={DECOMPOSITION_TREE_TEST_ID.nodeLink(dataNodeId, testIdScope)}
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            width: '100%',
            borderRadius: 0.5,
            minHeight: TREE_ITEM_MIN_HEIGHT,
            cursor: isInMoveMode ? (isDisabledAsParent ? 'not-allowed' : 'pointer') : undefined,
            opacity: isDisabledAsParent ? 0.5 : undefined
          }}
          aria-label={isInMoveMode ? 'Select as new parent' : 'Open details'}
          onClick={(event) => {
            if (isInMoveMode) {
              if (!isDisabledAsParent) onSelectParent?.(itemId);
              return;
            }
            event.stopPropagation();
            callbacks?.onOpenDetails?.(itemId);
          }}
        >
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 0.5,
              minWidth: 0,
              width: '100%'
            }}
          >
            {nodeIcon && <BlueprintIcon name={nodeIcon} fontSize='small' />}
            {meta?.autoId && (
              <Typography variant='body2' noWrap color='text.primary' sx={{ flexShrink: 0 }}>
                {meta.autoId}
              </Typography>
            )}
            {meta ? (
              <NodeTitle
                text={meta.title || meta.titlePlaceholder || ''}
                italic={!meta.title && Boolean(meta.titlePlaceholder)}
              />
            ) : (
              <NodeTitle text={treeItemProps.label} />
            )}
          </Box>
          {showMenu && (
            <Box className='tree-item-actions' sx={{ flexShrink: 0 }}>
              <NodeActionMenu
                nodeId={itemId}
                dataNodeId={dataNodeId}
                cb={callbacks}
                nodeName={nodeName ?? ''}
                onOpenChange={setIsMenuOpen}
              />
            </Box>
          )}
        </Box>
      }
    />
  );
});
